import { Directive, ElementRef, Input, OnInit, Renderer2, OnDestroy, Injector } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { finalize, firstValueFrom } from 'rxjs';
import { HelpCenterDocBean } from 'src/app/features/setting/pages/help-center-setting/model/HelpCenterDocBean';
import { HelpCenterService } from 'src/app/features/setting/service/help-center.service';

/**
 * 幫助中心下載按鈕指令
 * 用於下載 UserGuide 文件
 *
 * 使用範例：
 * <h1 [helpCenterDownloadButton]="permissionCode" [position]="'inline'">
 *   標題文字
 * </h1>
 */
@Directive({
  selector: '[helpCenterDownloadButton]'
})
export class HelpCenterDownloadButtonDirective implements OnInit, OnDestroy {

  @Input('helpCenterDownloadButton') permissionCode: string | null | undefined = null;
  @Input() position: 'inline' | 'right' = 'inline'; // inline: 在元素內, right: 絕對定位右上角
  @Input() lang: string = 'zh-tw';

  private userGuideDoc?: HelpCenterDocBean;
  private isLoading = false;
  private iconWrapper?: HTMLElement;
  private unlistenClick?: () => void;
  private unlistenKey?: () => void;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private helpCenterService: HelpCenterService,
    private messageService: MessageService,
    private translateService: TranslateService
  ) {}

  async ngOnInit(): Promise<void> {
    console.log('download btn loading: ' + this.permissionCode);

    if (!this.permissionCode) return;

    try {
      const docs = await firstValueFrom(
        this.helpCenterService.getGuideDocList(this.permissionCode, this.lang, 'UserGuide')
      );

      // 只取第一個文件（UserGuide）
      if (docs && docs.length > 0) {
        this.userGuideDoc = docs[0];
      }
    } catch (error) {
      console.error('載入檔案列表失敗', error);
      return;
    }

    // 沒有檔案就不顯示按鈕
    if (!this.userGuideDoc) {
      console.log('沒有可下載的 UserGuide');
      return;
    }

    // 避免重複掛
    if (this.iconWrapper) return;

    // 建立按鈕包裝器
    const iconWrapper = this.renderer.createElement('span');
    const icon = this.renderer.createElement('i');

    // 設定圖示樣式
    this.renderer.addClass(icon, 'pi');
    this.renderer.addClass(icon, 'pi-download');
    this.renderer.setStyle(icon, 'cursor', 'pointer');
    this.renderer.setStyle(icon, 'color', '#666');
    this.renderer.setStyle(icon, 'font-size', '1.4rem');
    this.renderer.setStyle(iconWrapper, 'right', '0.5rem');
    this.renderer.setAttribute(icon, 'tabindex', '0'); // 可鍵盤聚焦
    this.renderer.setStyle(icon, 'margin-left', '0.5rem');
    // Tooltip
    const tip = await firstValueFrom(
      this.translateService.get('HelpCenter.Tooltip.DownloadUserGuide')
    );
    this.renderer.setAttribute(icon, 'title', tip);

    // 根據 position 設定位置
    if (this.position === 'right') {
      this.renderer.setStyle(iconWrapper, 'position', 'absolute');
      this.renderer.setStyle(iconWrapper, 'top', '0.25rem');
      this.renderer.setStyle(iconWrapper, 'right', '0.5rem');
      this.renderer.setStyle(iconWrapper, 'z-index', '10');
      this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    } else {
      // inline 模式：靠右對齊
      this.renderer.setStyle(iconWrapper, 'margin-left', 'auto');
    }

    this.renderer.setStyle(iconWrapper, 'vertical-align', 'middle');

    // 綁定點擊事件
    this.unlistenClick = this.renderer.listen(icon, 'click', () => {
      void this.handleDownload();
    });

    // 綁定鍵盤事件
    this.unlistenKey = this.renderer.listen(icon, 'keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        void this.handleDownload();
      }
    });

    // 插入 DOM
    this.renderer.appendChild(iconWrapper, icon);
    this.renderer.appendChild(this.el.nativeElement, iconWrapper);
    this.iconWrapper = iconWrapper;

    console.log('下載按鈕已建立，文件名稱:', this.userGuideDoc.name);
  }

  ngOnDestroy(): void {
    // 清理事件監聽
    try {
      this.unlistenClick?.();
    } catch {}
    try {
      this.unlistenKey?.();
    } catch {}

    this.unlistenClick = this.unlistenKey = undefined;

    // 移除 DOM
    if (this.iconWrapper) {
      try {
        this.renderer.removeChild(this.el.nativeElement, this.iconWrapper);
      } catch {}
      this.iconWrapper = undefined;
    }
  }

  /**
   * 處理下載邏輯
   */
  private async handleDownload(): Promise<void> {
    if (this.isLoading) {
      console.log('下載中，請稍候...');
      return;
    }

    if (!this.userGuideDoc) {
      const msg = await firstValueFrom(
        this.translateService.get('HelpCenter.Message.NoFiles')
      );
      this.messageService.add({
        severity: 'info',
        summary: await firstValueFrom(this.translateService.get('Common.Toast.Info')),
        detail: msg
      });
      return;
    }

    // 下載 UserGuide
    this.isLoading = true;

    try {
      const file = await firstValueFrom(
        this.helpCenterService.downloadDoc(this.userGuideDoc.sn).pipe(
          finalize(() => (this.isLoading = false))
        )
      );

      this.saveFile(file, this.userGuideDoc.name ?? 'UserGuide');

      const successMsg = await firstValueFrom(
        this.translateService.get('HelpCenter.Message.DownloadSuccess')
      );
      this.messageService.add({
        severity: 'success',
        summary: await firstValueFrom(this.translateService.get('Common.Toast.Success')),
        detail: successMsg
      });
    } catch (error) {
      console.error('下載失敗', error);
      this.showError(error);
    }
  }

  /**
   * 儲存檔案到本地
   */
  private saveFile(file: Blob, filename: string): void {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * 顯示錯誤訊息
   */
  private async showError(resp: any): Promise<void> {
    const errorMsg = resp?.error?.message || resp?.error || '下載失敗';

    this.messageService.add({
      severity: 'error',
      summary: await firstValueFrom(this.translateService.get('Common.Toast.Error')),
      detail: errorMsg
    });
  }
}
